/*
 * The reads the shell and the inbox make, each named once: its key, its request and how long its
 * answer stays fresh (frontend performance plan, W4, F09 to F14). Every reader, prefetch and route
 * loader takes the same options from here, so a read made ahead is the read the screen then uses,
 * and a remount within its freshness asks nothing again.
 *
 * Identity. Keys name the workspace, the conversation and the filters a read depends on. The person
 * is not in them: the cache is one person's at a time, since the shell drops everything but the
 * session when the session turns out to be someone else's or no one's (`useIdentityGuard`). Plan ids
 * are unique across organizations (`work_plans.id` is the primary key), so a plan-keyed read cannot
 * answer for another tenant's plan.
 *
 * Freshness is per resource, not one blanket value, and is never a reason to skip the server's own
 * checks: each write is authorized where it lands.
 */
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { InboxListQuery } from "../src/inbox-v2/contract.ts";
import { IntegrationJobsResponseSchema } from "../src/integrations/contracts.ts";
import { getJson } from "./http.ts";
import { inboxApi } from "./inbox-v2/api.ts";
import { rememberWorkspaces } from "./workspace-slug.ts";

/** How long each answer is reused before a mount, a focus or a reconnect asks again (ms). */
const FRESHNESS = {
  /** Who is signed in and which workspaces they have: changed by an admin, rarely. */
  session: 30_000,
  directory: 30_000,
  /** What this person may reach in a workspace; the API decides every request regardless. */
  access: 30_000,
  /** Lists, counts and badges: new mail is found by the arrival check (W5), not by remounts. */
  inbox: 10_000,
  /** One conversation, and the reply agent's reading of it; prefetch and reader alike. */
  thread: 15_000,
  /** This person's saved views, changed only by this person. */
  views: 60_000,
  /** The typeahead's operators and the search index's coverage. */
  search: 30_000,
} as const;

export const SessionSchema = z
  .object({ user: z.object({ id: z.string(), name: z.string(), email: z.string() }) })
  .nullable();

export const WorkspacesSchema = z.object({
  workspaces: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      inbox: z.boolean().optional(),
      /** An older server's own count of this person's unseen replies; each badge is read apart now. */
      unseen: z.number().int().nonnegative().optional(),
    }),
  ),
});

export const sessionQuery = () =>
  queryOptions({
    queryKey: ["session"],
    staleTime: FRESHNESS.session,
    queryFn: async ({ signal }) =>
      SessionSchema.parse(await getJson("/api/auth/get-session", signal)),
  });

export const workspacesQuery = () =>
  queryOptions({
    queryKey: ["workspaces"],
    staleTime: FRESHNESS.directory,
    queryFn: async ({ signal }) => {
      const { workspaces } = WorkspacesSchema.parse(await getJson("/api/work/workspaces", signal));
      // Addresses use each workspace's slug from its newest name (`workspace-slug.ts`).
      rememberWorkspaces(workspaces);
      return workspaces;
    },
  });

export const accessQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["inbox-v2-access", workspaceId],
    staleTime: FRESHNESS.access,
    queryFn: ({ signal }) => inboxApi.access(workspaceId, signal),
  });

/** Everything one workspace's inbox has read: a write there names what of it changed (W5). */
export const inboxKey = (workspaceId: string) => ["inbox-v2", workspaceId] as const;

/** Everyone's totals under `counts`, one owner's beneath it, so a write reads both again. */
export const countsQuery = (workspaceId: string, owner: string | null = null) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "counts", ...(owner ? [owner] : [])],
    staleTime: FRESHNESS.inbox,
    queryFn: ({ signal }) => inboxApi.counts(workspaceId, signal, owner),
  });

/**
 * Who a thread here may be assigned to, and which of them is the viewer (card A4). Refused where
 * assignment is off, which the browser never asks: it reads this only once the counts carry "Mine".
 */
export const assigneesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "assignees"],
    staleTime: FRESHNESS.access,
    retry: false,
    queryFn: ({ signal }) => inboxApi.assignees(workspaceId, signal),
  });

export const needsReplyQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "needs-reply"],
    staleTime: FRESHNESS.inbox,
    queryFn: ({ signal }) => inboxApi.needsReply(workspaceId, signal),
  });

export const viewsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "views"],
    staleTime: FRESHNESS.views,
    queryFn: ({ signal }) => inboxApi.views(workspaceId, signal),
  });

/**
 * One page of a list, keyed by what it asks. The keys' hash ignores property order and absent
 * values, so two screens asking the same question (the Highlights strip and the empty pane's
 * shortlist) share one read.
 */
export const listQuery = (workspaceId: string, query: Partial<InboxListQuery>, offset = 0) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "list", query, offset],
    staleTime: FRESHNESS.inbox,
    queryFn: ({ signal }) => inboxApi.list(workspaceId, { ...query, offset }, signal),
  });

/** One thread; `withAssignee` where assignment is on adds its assignment (card A4). */
export const threadQuery = (workspaceId: string, planId: string, withAssignee = false) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "thread", planId, withAssignee],
    staleTime: FRESHNESS.thread,
    queryFn: ({ signal }) => inboxApi.thread(workspaceId, planId, signal, withAssignee),
  });

export const searchStatusQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "search-status"],
    staleTime: FRESHNESS.search,
    queryFn: ({ signal }) => inboxApi.searchStatus(workspaceId, signal),
  });

/** How often a workspace whose mail is still importing is read again (plan D-6). */
const IMPORT_STATUS_POLL_MS = 15_000;

/**
 * Whether this workspace's mail is still importing (LEA-483): read again every 15 seconds while it
 * is, and not again once it is complete. A failed read draws nothing and is not polled.
 */
export const importStatusQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "import-status"],
    queryFn: ({ signal }) => inboxApi.importStatus(workspaceId, signal),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === undefined || state === "complete" ? false : IMPORT_STATUS_POLL_MS;
    },
  });

export const suggestionsQuery = (workspaceId: string, word: string) =>
  queryOptions({
    queryKey: [...inboxKey(workspaceId), "suggest", word],
    staleTime: FRESHNESS.search,
    queryFn: ({ signal }) => inboxApi.suggest(workspaceId, word, signal),
  });

/**
 * A workspace's durable provider jobs: the explicit syncs a person pressed for (U01). One read for
 * every surface, so the mailbox button and "Read again" poll together, not each on their own.
 * Like every key here it names no person: the shell drops it when the session changes hands.
 */
export const integrationJobsKey = (workspaceId: string) =>
  ["integration-jobs", workspaceId] as const;
export const integrationJobsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: integrationJobsKey(workspaceId),
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `/api/work/workspaces/${encodeURIComponent(workspaceId)}/integration-jobs`,
        { credentials: "same-origin", signal },
      );
      // A build without integration jobs answers 409: no durable jobs to show.
      if (response.status === 409) return null;
      if (!response.ok) throw new Error("job_status_unavailable");
      return IntegrationJobsResponseSchema.parse(await response.json()).jobs;
    },
    retry: 2,
  });
